<template>
  <v-btn
    color="success"
    class="toolbar-action-btn ma-0"
    :loading="exporting"
    :disabled="!term"
    @click="exportToExcel"
  >
    <v-icon left small>mdi-file-excel</v-icon>
    Export
  </v-btn>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "SchoolSplitExport",
  props: {
    allStudents: { type: Array, default: () => [] },
    term: { type: String, default: "" },
    branch: { type: String, default: "" },
  },
  data() {
    return {
      exporting: false,
    };
  },
  methods: {
    fullName(first, last) {
      return (String(first || "").trim() + " " + String(last || "").trim()).trim();
    },
    async exportToExcel() {
      const term = String(this.term || "").trim();
      if (!term) return;

      this.exporting = true;
      try {
        const rows = this.buildRows(term);
        if (!rows.aoa.length) {
          this.$notify({
            text: "No data to export for the selected term.",
            group: "notification",
            type: "error",
          });
          return;
        }
        this.writeFile(rows.aoa, term);
      } catch (error) {
        this.$notify({
          text: "Failed to export data.",
          group: "notification",
          type: "error",
        });
      } finally {
        this.exporting = false;
      }
    },
    buildRows(term) {
      const branch = String(this.branch || "").trim();
      const students = (this.allStudents || []).filter((student) => {
        if (String(student.term || "").trim() !== term) return false;
        if (branch && String(student.branch || "").trim() !== branch) return false;
        return true;
      });

      const categoryNames = {};
      students.forEach((student) => {
        (student.flags || []).forEach((flag) => {
          if (flag.isDeleted) return;
          const name = String(flag.category || "").trim();
          if (name) categoryNames[name] = true;
        });
      });
      const categories = Object.keys(categoryNames).sort((a, b) =>
        a.localeCompare(b)
      );

      let maxExcludes = 0;
      students.forEach((student) => {
        const count = (student.excludes || []).filter((e) => !e.isDeleted).length;
        if (count > maxExcludes) maxExcludes = count;
      });

      const header = [
        "ID",
        "Hebrew Name",
        "English Name",
        "Branch",
        "Building",
        "Section",
        "Class",
      ].concat(categories);
      for (let i = 1; i <= maxExcludes; i++) {
        header.push("Exclude " + i + " ID", "Exclude " + i + " Name");
      }
      header.push("Class Enrolled");

      const aoa = students
        .slice()
        .sort((a, b) =>
          String(a.hebLastName || "").localeCompare(String(b.hebLastName || ""))
        )
        .map((student) => {
          const flagsByCategory = {};
          (student.flags || []).forEach((flag) => {
            if (flag.isDeleted) return;
            const category = String(flag.category || "").trim();
            if (category) flagsByCategory[category] = String(flag.flag || "").trim();
          });
          const excludes = (student.excludes || []).filter((e) => !e.isDeleted);
          const enrolled = (student.enrolledToClasses || [])
            .filter((row) => !row.isDeleted && row.classNumber != null)
            .map((row) => row.classNumber)
            .join(", ");

          const row = [
            student.studentId,
            this.fullName(student.hebFirstName, student.hebLastName),
            this.fullName(student.firstName, student.lastName),
            student.branch || "",
            student.building || "",
            student.section || "",
            student.class || "",
          ];
          categories.forEach((category) => {
            row.push(flagsByCategory[category] || "");
          });
          for (let i = 0; i < maxExcludes; i++) {
            const exclude = excludes[i];
            row.push(exclude ? exclude.studentId : "");
            row.push(
              exclude
                ? this.fullName(exclude.hebFirstName, exclude.hebLastName) ||
                    this.fullName(exclude.firstName, exclude.lastName)
                : ""
            );
          }
          row.push(enrolled);
          return row;
        });

      return { aoa: aoa.length ? [header].concat(aoa) : [] };
    },
    writeFile(aoa, term) {
      const branch = String(this.branch || "").trim();
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "School Split");
      const fileName =
        "school-split-" + term + (branch ? "-" + branch : "") + ".xlsx";
      XLSX.writeFile(workbook, fileName);
    },
  },
};
</script>
